// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import {CORET_SHOP_UPDATED_EVENT} from '@app/features/coret/CoretCallRewards';
import {
	type CoretShopItem,
	type CoretShopItemType,
	type CoretShopState,
	equipCoretShopItem,
	getCoretShop,
} from '@app/features/coret/CoretMediaApi';
import styles from '@app/features/coret/CoretProfileShopSettings.module.css';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {
	BoundingBoxIcon,
	CheckCircleIcon,
	FrameCornersIcon,
	IdentificationBadgeIcon,
	SparkleIcon,
	StorefrontIcon,
	TrophyIcon,
} from '@phosphor-icons/react';
import {useCallback, useEffect, useMemo, useState} from 'react';

const COSMETIC_TYPES: Array<{
	type: CoretShopItemType;
	title: string;
	description: string;
	button: string;
}> = [
	{
		type: 'avatar_frame',
		title: 'Moldura do avatar',
		description: 'Decora somente o contorno da sua foto e acompanha sua identidade no Coret.',
		button: 'Mudar moldura',
	},
	{
		type: 'profile_frame',
		title: 'Moldura do perfil',
		description: 'Decora o card completo quando alguém abre o seu perfil.',
		button: 'Mudar moldura do perfil',
	},
	{
		type: 'nameplate',
		title: 'Placa de identificação',
		description: 'Destaca seu nome nas listas de membros e nas mensagens diretas.',
		button: 'Mudar placa',
	},
	{
		type: 'profile_effect',
		title: 'Efeito do perfil',
		description: 'Reproduz uma animação quando alguém abre o seu cartão de perfil.',
		button: 'Mudar efeito',
	},
];

function CosmeticPreview({item, type}: {item: CoretShopItem | null; type: CoretShopItemType}) {
	const style = item
		? ({
				'--coret-cosmetic-accent': item.accent,
				'--coret-cosmetic-secondary': item.secondary,
				backgroundImage: item.asset_url ? `url(${item.asset_url})` : undefined,
			} as React.CSSProperties)
		: undefined;
	return (
		<div
			className={`${styles.cosmeticPreview} ${item && item.animation !== 'none' ? styles[`animation_${item.animation}`] : ''}`}
			style={style}
			aria-hidden="true"
		>
			{type === 'avatar_frame' ? <FrameCornersIcon weight="duotone" /> : null}
			{type === 'nameplate' ? <IdentificationBadgeIcon weight="duotone" /> : null}
			{type === 'profile_frame' ? <BoundingBoxIcon weight="duotone" /> : null}
			{type === 'profile_effect' ? <SparkleIcon weight="duotone" /> : null}
		</div>
	);
}

export function CoretProfileShopSettings() {
	const [state, setState] = useState<CoretShopState | null>(null);
	const [editingType, setEditingType] = useState<CoretShopItemType | null>(null);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState<string | null>(null);

	const applyState = useCallback((next: CoretShopState) => {
		setState(next);
		window.dispatchEvent(new CustomEvent(CORET_SHOP_UPDATED_EVENT, {detail: next}));
	}, []);

	useEffect(() => {
		let active = true;
		void getCoretShop()
			.then((next) => active && setState(next))
			.catch(
				(error) =>
					active && setMessage(error instanceof Error ? error.message : 'Não foi possível carregar seus itens.'),
			);
		const onUpdate = (event: Event) => {
			const next = (event as CustomEvent<CoretShopState>).detail;
			if (next) setState(next);
		};
		window.addEventListener(CORET_SHOP_UPDATED_EVENT, onUpdate);
		return () => {
			active = false;
			window.removeEventListener(CORET_SHOP_UPDATED_EVENT, onUpdate);
		};
	}, []);

	const ownedByType = useMemo(() => {
		const result = new Map<CoretShopItemType, Array<CoretShopItem>>();
		for (const entry of COSMETIC_TYPES) result.set(entry.type, []);
		if (!state) return result;
		for (const item of state.items) {
			if (state.owned_item_ids.includes(item.id)) result.get(item.type)?.push(item);
		}
		return result;
	}, [state]);

	const equippedItem = useCallback(
		(type: CoretShopItemType) => {
			if (!state) return null;
			const equippedId = state.equipped[type];
			return state.items.find((item) => item.id === equippedId) ?? null;
		},
		[state],
	);

	const openPicker = (type: CoretShopItemType) => {
		setEditingType(type);
		setSelectedId(state?.equipped[type] ?? null);
		setMessage(null);
	};

	const openStore = () => {
		ModalCommands.popAll();
		RouterUtils.transitionTo(Routes.STORE);
	};

	const applySelection = async () => {
		if (!editingType) return;
		setBusy(true);
		setMessage(null);
		try {
			applyState(await equipCoretShopItem(editingType, selectedId));
			setEditingType(null);
			setMessage('Personalização aplicada ao seu perfil.');
		} catch (error) {
			setMessage(error instanceof Error ? error.message : 'Não foi possível aplicar este item.');
		} finally {
			setBusy(false);
		}
	};

	return (
		<section className={styles.section} aria-labelledby="coret-profile-items-title">
			<div className={styles.header}>
				<div>
					<h3 id="coret-profile-items-title">Itens do perfil</h3>
					<p>Use aqui os itens comprados na Loja Coret.</p>
				</div>
				<button type="button" className={styles.storeButton} onClick={openStore}>
					<StorefrontIcon weight="fill" /> Abrir Loja
				</button>
			</div>

			<div className={styles.categoryList}>
				{COSMETIC_TYPES.map((entry) => {
					const item = equippedItem(entry.type);
					return (
						<div key={entry.type} className={styles.categoryCard}>
							<CosmeticPreview item={item} type={entry.type} />
							<div className={styles.categoryText}>
								<strong>{entry.title}</strong>
								<span>{item?.name ?? 'Nenhum item equipado'}</span>
								<small>{entry.description}</small>
							</div>
							<button type="button" onClick={() => openPicker(entry.type)} disabled={!state}>
								{entry.button}
							</button>
						</div>
					);
				})}
			</div>

			<div className={styles.collectiblesSection}>
				<div className={styles.collectiblesHeader}>
					<TrophyIcon weight="fill" />
					<div>
						<strong>Minha coleção</strong>
						<span>Cartas, mascotes, troféus e emblemas conquistados.</span>
					</div>
				</div>
				<div className={styles.collectiblesGrid}>
					{state?.community.collectibles
						.filter((item) => state.community.owned_collectible_ids.includes(item.id))
						.map((item) => (
							<article key={item.id} className={styles.collectibleCard}>
								{item.asset_url ? <img src={item.asset_url} alt="" /> : <TrophyIcon weight="duotone" />}
								<strong>{item.name}</strong>
								<span>
									{item.kind} · {item.rarity}
								</span>
								{item.description ? <p>{item.description}</p> : null}
							</article>
						))}
				</div>
				{state && state.community.owned_collectible_ids.length === 0 ? (
					<p className={styles.emptyText}>Você ainda não possui colecionáveis.</p>
				) : null}
			</div>

			{editingType ? (
				<div className={styles.picker}>
					<div className={styles.pickerHeader}>
						<div>
							<strong>{COSMETIC_TYPES.find((entry) => entry.type === editingType)?.button}</strong>
							<span>Selecione um item do seu inventário e aplique.</span>
						</div>
						<button type="button" className={styles.linkButton} onClick={openStore}>
							Ver itens na Loja
						</button>
					</div>
					<div className={styles.pickerGrid}>
						<button
							type="button"
							className={selectedId === null ? styles.selectedItem : ''}
							onClick={() => setSelectedId(null)}
						>
							<div className={styles.nonePreview}>Sem item</div>
							<span>Nenhum</span>
							{selectedId === null ? <CheckCircleIcon weight="fill" /> : null}
						</button>
						{(ownedByType.get(editingType) ?? []).map((item) => (
							<button
								key={item.id}
								type="button"
								className={selectedId === item.id ? styles.selectedItem : ''}
								onClick={() => setSelectedId(item.id)}
							>
								<CosmeticPreview item={item} type={item.type} />
								<span>{item.name}</span>
								{selectedId === item.id ? <CheckCircleIcon weight="fill" /> : null}
							</button>
						))}
					</div>
					{(ownedByType.get(editingType) ?? []).length === 0 ? (
						<p className={styles.emptyText}>Você ainda não possui itens desta categoria.</p>
					) : null}
					<div className={styles.pickerActions}>
						<button type="button" className={styles.cancelButton} onClick={() => setEditingType(null)} disabled={busy}>
							Cancelar
						</button>
						<button type="button" className={styles.applyButton} onClick={() => void applySelection()} disabled={busy}>
							{busy ? 'Aplicando…' : 'Aplicar'}
						</button>
					</div>
				</div>
			) : null}

			{message ? <p className={styles.message}>{message}</p> : null}
		</section>
	);
}
